import { formatRate, type RateUnit } from '@/core/display-settings'
import { widgetCopy } from '@/i18n/widget-copy'
import type { AppLocale } from '@/i18n/messages'
import { streamMessages } from '@/i18n/stream'
import type { ExpRateTrendPresentation } from './exp-rate-trend'
import type { ExpReadoutPresentation } from './exp-readout-presentation'
import type { ExpRateChartSnapshot } from '@/domain/types'
import { expChartSample } from './exp-chart-sample'
import { elapsedLayoutWidth } from './widget-elapsed'

export interface StreamHudData {
  expChart?: ExpRateChartSnapshot
  rateUnit?: RateUnit
  sessionId: string | null
  status: string
  statusText: string
  elapsed: string
  level: string
  jobName?: string
  expRate: string
  expReadout?: ExpReadoutPresentation
  /** Raw displayed mean, including the last confirmed value during brief checks. */
  expDisplayRateValue?: number | null
  expRateValue?: number | null
  expRateTrend?: ExpRateTrendPresentation
  expPercent: string
  expRatio: number
  eta: string
  expGained: string
  expGainedLabel: string
  fullDuration: string
  mesosRate: string
  mesosNet: string
  mesosStart: string
  mesosBalanceChange: string
  hpPercent: string
  hpRatio: number
  mpPercent: string
  mpRatio: number
  map: string
  enabled: { mesos: boolean; hp: boolean; mp: boolean; mapName: boolean }
  levelUpId?: string
}

export const STREAM_ASSETS = Object.freeze({
  exp: '/stream-assets/exp.png', mesos: '/stream-assets/mesos.png',
  hp: '/stream-assets/hp.png', mp: '/stream-assets/mp.png', mushroom: '/stream-assets/mushroom.png',
})

export type StreamShape = 'classic' | 'strip' | 'portrait'

/** Only text/visibility with a layout footprint; trend animation owns a fixed box. */
export function streamLayoutContent(data: StreamHudData): string {
  return JSON.stringify([data.rateUnit, elapsedLayoutWidth(data.elapsed), data.level, data.jobName, data.expRate,
    data.expPercent, data.eta, data.expGained, data.expGainedLabel, !!data.expReadout?.netUnavailableHint, data.fullDuration,
    data.mesosRate, data.mesosStart, data.mesosBalanceChange, data.hpPercent, data.mpPercent,
    data.map, data.enabled])
}
export function streamPreviewData(locale: AppLocale, rateUnit: RateUnit = 'hour'): StreamHudData {
  const compact = (value: number) => new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(value)
  return {
    expChart: expChartSample(), rateUnit, sessionId: 'preview', status: 'running', statusText: streamMessages[locale].recording, elapsed: '01:47:37', level: '75',
    jobName: { 'zh-CN': '祭司', 'zh-TW': '祭司', en: 'Priest', ja: 'プリースト', ko: '프리스트' }[locale],
    expRate: formatRate(304400, locale, rateUnit, { maximumFractionDigits: 1 }), expDisplayRateValue: 304400, expRateValue: 304400, expPercent: '44.15%', expRatio: .4415, eta: widgetCopy(locale, '4h 57m', '4小时57分'),
    expGained: `+${compact(68200)}`, expGainedLabel: streamMessages[locale].gained, fullDuration: widgetCopy(locale, '8h 51m', '8小时51分'),
    mesosRate: formatRate(213200, locale, rateUnit, { maximumFractionDigits: 1 }), mesosNet: `+${compact(47600)}`, mesosStart: compact(4336560), mesosBalanceChange: `+${compact(47600)}`,
    hpPercent: '53%', hpRatio: .53, mpPercent: '49%', mpRatio: .49,
    map: widgetCopy(locale, 'Victoria Island / Forest of Wisdom', '金银岛 / 智慧森林'), enabled: { mesos: true, hp: true, mp: true, mapName: true },
  }
}
